import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import { IModel } from 'app/model/base/annotation';
import _ from 'lodash';
import { IApiIntegrationActivity, ReferenceAdapter } from 'shell-models-lib';
import { ApiIntegrationActivity } from 'app/model/pipeline/api-integration.activity';
import { ValueAdapter } from '../value.adapter';
import { getFabricLsForParameterizedConnections } from '../utils';
import { IApiIntegrationActivityContext } from 'app/interfaces/copilot.pipeline.action.types';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ApiIntegrationActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            serviceType: '*',
            itemType: '*',
            workspaceId: '*',
            itemId: '*',
            apiRequest: '*',
            apiParameters: '*'
        }
    }
})
export class ApiIntegrationActivityAdapter extends ActivityAsyncAdapter<ApiIntegrationActivity, IApiIntegrationActivity, IApiIntegrationActivityContext> {
    public model(): IModel {
        return Metamodel.modelFor(ApiIntegrationActivity);
    }

    public override async toFabricCopilotContext(activity: ApiIntegrationActivity): Promise<IApiIntegrationActivityContext> {
        if (activity) {
            const activityContext: IApiIntegrationActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.ServiceType = ValueAdapter.toResource(activity, ApiIntegrationActivity.SERVICE_TYPE, true);
            activityContext.ItemType = ValueAdapter.toResource(activity, ApiIntegrationActivity.ITEM_TYPE, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, ApiIntegrationActivity.WORKSPACE_ID, true);
            activityContext.ApiRequest = ValueAdapter.toResource(activity, ApiIntegrationActivity.API_REQUEST, true);
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IApiIntegrationActivityContext,
        model?: ApiIntegrationActivity
    ): Promise<ApiIntegrationActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, ApiIntegrationActivity.SERVICE_TYPE, context.ServiceType);
            ValueAdapter.toModel(model, ApiIntegrationActivity.ITEM_TYPE, context.ItemType);
            ValueAdapter.toModel(model, ApiIntegrationActivity.WORKSPACE_ID, context.WorkspaceId);
            ValueAdapter.toModel(model, ApiIntegrationActivity.API_REQUEST, context.ApiRequest);
        }
        return model;
    }

    protected async _toResource(activity: ApiIntegrationActivity): Promise<IApiIntegrationActivity> {
        const modelApiIntegrationActivity = super._getCommonActivityProperties(
            activity, this.model().name) as IApiIntegrationActivity;

        modelApiIntegrationActivity.typeProperties = {
            serviceType: ValueAdapter.toResource(activity, ApiIntegrationActivity.SERVICE_TYPE),
            itemType: ValueAdapter.toResource(activity, ApiIntegrationActivity.ITEM_TYPE),
            workspaceId: ValueAdapter.toResource(activity, ApiIntegrationActivity.WORKSPACE_ID),
            apiRequest: ValueAdapter.toResource(activity, ApiIntegrationActivity.API_REQUEST),
            apiParameters: ValueAdapter.toResource(activity, ApiIntegrationActivity.API_PARAMETERS)
        };

        modelApiIntegrationActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.fabricWebLinkedService,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelApiIntegrationActivity, parsed);
        }

        return modelApiIntegrationActivity;
    }

    protected async _toModel(modelActivity: IApiIntegrationActivity): Promise<ApiIntegrationActivity> {
        const activity = this._createActivity(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, ApiIntegrationActivity.SERVICE_TYPE, modelActivity.typeProperties.serviceType);
        ValueAdapter.toModel(activity, ApiIntegrationActivity.ITEM_TYPE, modelActivity.typeProperties.itemType);
        ValueAdapter.toModel(activity, ApiIntegrationActivity.WORKSPACE_ID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, ApiIntegrationActivity.API_REQUEST, modelActivity.typeProperties.apiRequest);
        ValueAdapter.toModel(activity, ApiIntegrationActivity.API_PARAMETERS, modelActivity.typeProperties.apiParameters);
        this._setFabricWebConnection(activity, modelActivity);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, this.model()));
        return activity;
    }

    protected _createActivity(name: string, description?: string): ApiIntegrationActivity {
        return ApiIntegrationActivity.MODEL.create<ApiIntegrationActivity>(name, description);
    }

    private _setFabricWebConnection(activity: ApiIntegrationActivity, modelActivity: IApiIntegrationActivity): void {
        if (modelActivity.linkedServiceName !== undefined) {
            const linkedService = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                adfModelTypeMap.rest.linkedServiceType,
                activity);
            if (linkedService instanceof RestServiceLinkedService) {
                activity.fabricWebLinkedService = linkedService;
            }

            if (!activity.fabricWebLinkedService) {
                activity.fabricWebLinkedService = ReferenceAdapter.toProxyModel<RestServiceLinkedService>(
                    Metamodel.modelFor(RestServiceLinkedService),
                    modelActivity.linkedServiceName,
                    this.context
                );
            }
        }
    }
}
